import { ForwardedRef, JSXElementConstructor, useMemo } from 'react';

import { match, MatchFunction } from 'path-to-regexp';

import { HrefProp, Locale, RouteLinkProps, RouterConfig } from './types';
import { useRouterLinkResolver } from './useRouterLinkResolver';

export type RouterServiceSingleton = {
  useRouteInfo: () => {
    pathname: string;
    asPath: string;
    query: Record<string, string | undefined>;
  };
  useRouteNavigation: () => {
    push: (options: { pathname: string; locale: Locale }) => Promise<boolean>;
    replace: (options: {
      pathname: string;
      locale: Locale;
    }) => Promise<boolean>;
    back: () => Promise<boolean>;
  };
  RouteLink: JSXElementConstructor<
    Omit<RouteLinkProps, 'href'> & {
      href: string;
      ref?: ForwardedRef<HTMLAnchorElement>;
    }
  >;
  useLocale?: () => Locale;
  config?: RouterConfig;
  baseUrl?: string;
};

let singleton: RouterServiceSingleton & {
  pathsToPage?: { routeKey: string; match: MatchFunction }[];
};

export const getRouterServiceSingleton = () => {
  if (!singleton) {
    throw new Error('RouterService must be initialize before using it');
  }

  return singleton;
};

const toHrefProp = (location: string) => {
  const pathsToPage = getRouterServiceSingleton().pathsToPage;

  if (pathsToPage) {
    for (const { match, routeKey } of pathsToPage) {
      const matched = match(location.split('?')[0]);
      if (matched) {
        return {
          routeKey,
          params: matched.params as Record<string, unknown>,
        };
      }
    }
  }
  return undefined;
};

/**
 * Retourne les infos concernant la route courante
 */
export const useRouteInfo = <T = Record<string, string | undefined>,>(): {
  pathname: string;
  asPath: string;
  query: T;
  href?: HrefProp;
} => {
  const { pathname, asPath, query } =
    getRouterServiceSingleton().useRouteInfo();

  const queryString = JSON.stringify(query);

  const res = useMemo(() => {
    const href = toHrefProp(pathname);

    return {
      pathname,
      asPath,
      query: { ...(href?.params || {}), ...query } as T,
      href,
    };
  }, [asPath, queryString, pathname, query]);

  return res;
};

/**
 * Retourne les méthodes pour naviguer entre les écrans
 */
export const useRouteNavigation = (): {
  push: (options: RouteLinkProps) => Promise<boolean>;
  replace: (options: RouteLinkProps) => Promise<boolean>;
  back: () => Promise<boolean>;
} => {
  const routeNavigation = getRouterServiceSingleton().useRouteNavigation();
  const { routerLinkResolver } = useRouterLinkResolver();

  const res = useMemo(() => {
    return {
      push: async (options: RouteLinkProps) => {
        const linkProps = routerLinkResolver(options);

        if (linkProps) {
          return await routeNavigation.push({
            ...options,
            pathname: linkProps.href,
            locale: linkProps.locale,
          });
        }
        return Promise.resolve(false);
      },
      replace: async (options: RouteLinkProps) => {
        const linkProps = routerLinkResolver(options);
        if (linkProps) {
          return await routeNavigation.replace({
            ...options,
            pathname: linkProps.href,
            locale: linkProps.locale,
          });
        }
        return Promise.resolve(false);
      },
      back: async () => await routeNavigation.back(),
    };
  }, [routerLinkResolver]);

  return res;
};

export const RouterService = {
  init: (routerService: RouterServiceSingleton) => {
    singleton = routerService;

    if (routerService.config) {
      singleton.pathsToPage = Object.entries(routerService.config).reduce<
        {
          routeKey: string;
          match: MatchFunction;
        }[]
      >((acc, [routeKey, value]) => {
        if (!value.pathname) {
          return acc;
        }

        return [
          ...acc,
          {
            routeKey,
            match: match(value.pathname, { decode: decodeURIComponent }),
          },
        ];
      }, []);
    }
  },
};
